<template>
  <div class="w-full h-full relative">
    <!-- Hint de modo activo -->
    <div
      v-if="hint"
      class="absolute surface-card border-round p-2 shadow-2 text-xs text-center"
      style="top:10px;left:50%;transform:translateX(-50%);z-index:1000;pointer-events:none;white-space:nowrap"
    >
      {{ hint }}
    </div>

    <MapaBase
      ref="mapBase"
      class="w-full h-full"
      :terrID="terrID"
      :center="center"
      :color="color"
      @mapClick="onMapClick"
      @ready="onMapReady"
      v-if="show"
    />
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted, computed } from 'vue'
import MapaBase from '@/components/MapaBase/MapaBase.vue'

const props = defineProps({
  lineas: { type: Array, default: () => [] },
  lineaActivaId: { type: String, default: null },
  sentidoActivo: { type: String, default: 'ida' },
  modo: { type: String, default: 'paradas' }, // 'paradas' | 'recorrido'
  center: [Array, Object],
  color: Object,
  terrID: String,
  origenLatlng: Object,
  destinoLatlng: Object,
})

const emit = defineEmits(['mapClick', 'ready'])

const mapBase = ref(null)
const show = ref(false)
const mapReady = ref(false)

// Estado interno de Leaflet
const paradaMarkers = []
const polylineRefs = {} // { lineaId: { ida: L.polyline, vuelta: L.polyline } }
const buscadorMarkers = [] // origen, destino

const hint = computed(() => {
  if (!props.lineaActivaId) return null
  if (props.modo === 'paradas') return 'Clic en el mapa para agregar parada'
  return 'Clic en el mapa para trazar el recorrido'
})

const m = () => mapBase.value?.m?.map?.value
const L = () => mapBase.value?.m?.L

// --- Limpieza ---
const clearParadas = () => {
  const map = m()
  paradaMarkers.forEach((mk) => map?.removeLayer(mk))
  paradaMarkers.splice(0)
}

const clearPolylines = () => {
  const map = m()
  Object.values(polylineRefs).forEach(({ ida, vuelta }) => {
    if (ida) map?.removeLayer(ida)
    if (vuelta) map?.removeLayer(vuelta)
  })
  Object.keys(polylineRefs).forEach((k) => delete polylineRefs[k])
}

const clearBuscador = () => {
  const map = m()
  buscadorMarkers.forEach((mk) => map?.removeLayer(mk))
  buscadorMarkers.splice(0)
}

// --- Renderizado ---
const renderPolylines = () => {
  const map = m()
  const Leaflet = L()
  if (!map || !Leaflet) return

  clearPolylines()

  for (const linea of props.lineas) {
    const esActiva = linea.id === props.lineaActivaId
    polylineRefs[linea.id] = { ida: null, vuelta: null }

    for (const sentido of ['ida', 'vuelta']) {
      const pts = linea[sentido]?.recorrido || []
      if (pts.length < 2) continue

      const esActivaSentido = esActiva && props.sentidoActivo === sentido
      const weight = esActivaSentido ? 6 : 3
      const opacity = esActivaSentido ? 0.9 : esActiva ? 0.5 : 0.35
      const dashArray = !esActivaSentido && esActiva ? '8 6' : null

      const pl = Leaflet.polyline(pts, {
        color: linea.color || '#3388ff',
        weight,
        opacity,
        dashArray,
      }).addTo(map)

      polylineRefs[linea.id][sentido] = pl
    }
  }
}

const renderParadas = () => {
  const map = m()
  const Leaflet = L()
  if (!map || !Leaflet) return

  clearParadas()

  const lineaActiva = props.lineas.find((l) => l.id === props.lineaActivaId)
  if (!lineaActiva) return

  const paradas = lineaActiva[props.sentidoActivo]?.paradas || []
  paradas.forEach((parada, idx) => {
    const latlng = parada.latlng
    if (!latlng) return

    const marker = Leaflet.circleMarker(latlng, {
      radius: 7,
      color: lineaActiva.color || '#3388ff',
      fillColor: lineaActiva.color || '#3388ff',
      fillOpacity: 0.85,
      weight: 2,
      draggable: false,
    })
      .bindTooltip(
        `${lineaActiva.numero} (${props.sentidoActivo}) — Parada ${idx + 1}${parada.nombre ? ': ' + parada.nombre : ''}`,
      )
      .addTo(map)

    // Hacer arrastrable manualmente
    const draggableMarker = Leaflet.marker(latlng, {
      opacity: 0,
      draggable: true,
    }).addTo(map)

    draggableMarker.on('drag', (e) => {
      const pos = e.target.getLatLng()
      marker.setLatLng(pos)
    })
    draggableMarker.on('dragend', (e) => {
      parada.latlng = e.target.getLatLng()
    })
    draggableMarker.on('dblclick', () => {
      map.removeLayer(marker)
      map.removeLayer(draggableMarker)
      paradas.splice(idx, 1)
    })

    paradaMarkers.push(marker, draggableMarker)
  })
}

const renderBuscador = () => {
  const map = m()
  const Leaflet = L()
  if (!map || !Leaflet) return

  clearBuscador()

  if (props.origenLatlng) {
    const mk = Leaflet.circleMarker(props.origenLatlng, {
      radius: 9,
      color: '#27ae60',
      fillColor: '#27ae60',
      fillOpacity: 0.9,
      weight: 2,
    })
      .bindTooltip('Origen', { permanent: false })
      .addTo(map)
    buscadorMarkers.push(mk)
  }

  if (props.destinoLatlng) {
    const mk = Leaflet.circleMarker(props.destinoLatlng, {
      radius: 9,
      color: '#c0392b',
      fillColor: '#c0392b',
      fillOpacity: 0.9,
      weight: 2,
    })
      .bindTooltip('Destino', { permanent: false })
      .addTo(map)
    buscadorMarkers.push(mk)
  }
}

const renderTodo = () => {
  if (!mapReady.value) return
  renderPolylines()
  renderParadas()
  renderBuscador()
}

// --- Eventos ---
const onMapClick = (e) => {
  emit('mapClick', e.latlng)
}

const onMapReady = () => {
  mapReady.value = true
  renderTodo()
  emit('ready')
}

onUnmounted(() => {
  clearParadas()
  clearPolylines()
  clearBuscador()
})

// --- Watches ---
// Re-renderizar cuando cambia la línea activa, sentido o modo
watch(
  () => [props.lineaActivaId, props.sentidoActivo, props.modo],
  () => renderTodo(),
)

// Re-renderizar cuando cambia la cantidad de líneas o sus paradas/recorridos
watch(
  () => {
    const active = props.lineas.find((l) => l.id === props.lineaActivaId)
    return [
      props.lineas.length,
      active?.[props.sentidoActivo]?.paradas?.length,
      active?.[props.sentidoActivo]?.recorrido?.length,
      active?.[props.sentidoActivo === 'ida' ? 'vuelta' : 'ida']?.recorrido?.length,
    ]
  },
  () => renderTodo(),
)

// Re-renderizar marcadores de buscador cuando cambian las coordenadas
watch(
  () => [props.origenLatlng, props.destinoLatlng],
  () => renderBuscador(),
)

// Inicializar con delay para que MapaBase monte correctamente
const init = () => {
  show.value = true
}
init()
</script>
